import React from 'react';
import { mdiArrowDown, mdiArrowUp } from '@mdi/js';
import Icon from '@mdi/react';

/**
 * Renders a direction indicator anchored to a floorplan pin tip.
 *
 * Intended to be rendered INSIDE a pin container that already has
 * `transform: translate(-50%, -100%) scale(pinScale)` with
 * `transformOrigin: '50% 100%'`. This component uses position:absolute
 * so it does not affect the pin container's layout dimensions.
 *
 * direction:
 *   null / undefined  → renders nothing
 *   'up'              → upward arrow icon (camera points at ceiling)
 *   'down'            → downward arrow icon (camera points at floor)
 *   number 0–359      → SVG wedge cone (44° FOV), 0 = north (up on map), clockwise
 *
 * zoom: current map zoom level (>= 1, default 1). The cone grows moderately
 *   with zoom so it stays legible when the map is zoomed in, but less than
 *   the underlying image (net screen scale = zoom^0.3).
 */

const PIN_TIP_OFFSET_PX = 8;

// Base cone size in px (pre-zoom scaling). This is the SVG element's height
// at zoom=1, measured in the pin-container's local coordinate space (which
// the parent already counter-scales by 1/zoom so it stays constant on screen).
const CONE_BASE_PX = 50;

// SVG-coordinate geometry of the cone (independent of pixel size / zoom).
// CONE_SVG_LENGTH:     arc radius ≈ distance from tip to far edge.
// CONE_SVG_HALF_WIDTH: half-width of the cone opening at the far edge.
// CONE_SVG_FAR_Y:      derived y-offset of the far edge (point lies on the arc).
const CONE_SVG_LENGTH = 70;
const CONE_SVG_HALF_WIDTH = 12;
const CONE_SVG_FAR_Y = Math.sqrt(
  CONE_SVG_LENGTH ** 2 - CONE_SVG_HALF_WIDTH ** 2
);

export function FloorplanDirectionCone({
  direction,
  zoom = 1,
  color = 'rgba(211, 47, 47, 0.45)',
  strokeColor = 'rgba(211, 47, 47, 0.8)',
  pinTipOffsetPx = PIN_TIP_OFFSET_PX,
}) {
  if (direction === null || direction === undefined) return null;

  if (direction === 'up' || direction === 'down') {
    return (
      <div
        style={{
          position: 'absolute',
          top: '90%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 16,
          height: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: strokeColor,
          borderRadius: '50%',
          pointerEvents: 'none',
          zIndex: 4,
        }}
      >
        <Icon
          path={direction === 'up' ? mdiArrowUp : mdiArrowDown}
          size={0.5}
          color="#fff"
        />
      </div>
    );
  }

  const coneSize = CONE_BASE_PX * Math.max(1, Math.pow(zoom, 0.4));

  // Position: the cone wrapper is centered horizontally and its bottom edge
  // sits `PIN_TIP_OFFSET_PX` above the pin container's bottom, so the SVG
  // tip (which is at the very bottom of the viewBox) lands on the marker tip.
  return (
    <div
      style={{
        position: 'absolute',
        bottom: `${pinTipOffsetPx}px`,
        left: '50%',
        transform: 'translateX(-50%)',
        width: coneSize,
        height: coneSize,
        pointerEvents: 'none',
        overflow: 'visible',
        zIndex: 4,
      }}
    >
      <svg
        viewBox="-50 -100 100 100"
        width={coneSize}
        height={coneSize}
        style={{
          display: 'block',
          overflow: 'visible',
          transform: `rotate(${direction}deg)`,
          transformOrigin: '50% 100%',
        }}
      >
        <defs>
          <linearGradient
            id={`coneGrad-${direction}`}
            gradientUnits="objectBoundingBox"
            x1="0"
            y1="1"
            x2="0"
            y2="0"
          >
            <stop offset="0%" stopColor={color} stopOpacity="1" />
            <stop offset="70%" stopColor={color} stopOpacity="1" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
          <linearGradient
            id={`coneStroke-${direction}`}
            gradientUnits="objectBoundingBox"
            x1="0"
            y1="1"
            x2="0"
            y2="0"
          >
            <stop offset="0%" stopColor={strokeColor} stopOpacity="1" />
            <stop offset="80%" stopColor={strokeColor} stopOpacity="1" />
            <stop offset="100%" stopColor={strokeColor} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d={`M 0 0 L -${CONE_SVG_HALF_WIDTH} -${CONE_SVG_FAR_Y} A ${CONE_SVG_LENGTH} ${CONE_SVG_LENGTH} 0 0 1 ${CONE_SVG_HALF_WIDTH} -${CONE_SVG_FAR_Y} Z`}
          fill={`url(#coneGrad-${direction})`}
          stroke={`url(#coneStroke-${direction})`}
          strokeWidth="1.5"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}
